/**
 * Cloud Operations — Deployment detail.
 *
 * Header with status pill + subtabs (Overview / Resources / Runs).
 * Reads /deployments/{id} + /deployments/{id}/runs.
 *
 * Per design doc Mockup §"Deployment detail" + §5 (GET endpoints).
 */

import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  getDeployment, listDeploymentRuns, getTarget, getSystemWorkflow,
} from '../../services/cloudOperationsApi';
import {
  T, btn, btnSm, Banner, Th, Td,
  StatusPill, ModePill, ProviderPill, fmtDate, shortId,
} from './_ui';
import WorkflowCanvasReadView from './WorkflowCanvasReadView';
import { createReconnectingEventSource } from '../../lib/reconnectingEventSource';

export default function DeploymentDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [t, setT] = useState(null);
  const [runs, setRuns] = useState([]);
  // workflow.definition lazy-fetched the first time the user clicks the
  // Workflow tab. Avoids paying a fetch on every Detail page load when most
  // operators just want runs + status.
  const [workflowDef, setWorkflowDef] = useState(null);
  const [workflowLoading, setWorkflowLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getDeployment(id)
      .then((dep) => {
        if (!alive) return;
        setD(dep);
        // Best-effort target fetch (404s if retired — fine)
        if (dep.target_id) {
          getTarget(dep.target_id).then((tgt) => alive && setT(tgt)).catch(() => {});
        }
        return listDeploymentRuns(id, { size: 50 });
      })
      .then((r) => { if (alive && r) setRuns(r?.items || []); })
      .catch((e) => { if (alive) setError(e?.message || String(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  // Live SSE — when the most recent run is in_progress, open an EventSource
  // and merge progress / completed events into the runs list as they arrive.
  // Polling fallback: if SSE drops or the browser blocks EventSource, the
  // page still has the runs list from the initial fetch above.
  useEffect(() => {
    const live = runs.find((r) => r?.result === 'in_progress');
    if (!live) return undefined;
    const onProgress = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        setRuns((prev) => prev.map((r) => r.id === payload.run_id ? {
          ...r,
          completed_steps:   payload.completed_steps   ?? r.completed_steps,
          total_steps:       payload.total_steps       ?? r.total_steps,
          current_step_name: payload.current_step_name ?? r.current_step_name,
        } : r));
      } catch (_e) { /* ignore malformed frame */ }
    };
    const onCompleted = (evt) => {
      try {
        const payload = JSON.parse(evt.data);
        setRuns((prev) => prev.map((r) => r.id === payload.run_id ? {
          ...r,
          result:         payload.result ?? r.result,
          failure_reason: payload.failure_reason ?? r.failure_reason,
        } : r));
      } catch (_e) { /* ignore */ }
    };
    // Storm-safe reconnect (capped backoff) via the shared helper instead of
    // the browser's native uncapped retry — see lib/reconnectingEventSource.
    const conn = createReconnectingEventSource({
      url: `/api/v1/cloud-operations/runs/${live.id}/events`,
      events: { progress: onProgress, completed: onCompleted },
    });
    return () => { conn.close(); };
  }, [runs]);

  if (loading) return <Banner color={T.info} bg={T.infoBg}>Loading deployment…</Banner>;
  if (error)   return <Banner color={T.danger} bg={T.dangerBg}>{error}</Banner>;
  if (!d)      return <Banner color={T.warn} bg={T.warnBg}>Deployment not found.</Banner>;

  return (
    <div>
      <Breadcrumb />
      <Header dep={d} target={t} onBack={() => nav('/cloud-operations/deployments')} />
      <Tabs
        current={tab}
        onChange={(next) => {
          setTab(next);
          // Lazy-load the workflow definition the first time the Workflow
          // tab is opened. Avoids paying the fetch when nobody uses it.
          if (next === 'workflow' && !workflowDef && !workflowLoading && d?.workflow_source_id) {
            setWorkflowLoading(true);
            getSystemWorkflow(d.workflow_source_id)
              .then((w) => setWorkflowDef(w?.definition || w?.definition_json || null))
              .finally(() => setWorkflowLoading(false));
          }
        }}
      />
      {tab === 'overview'  && <Overview dep={d} target={t} runs={runs} />}
      {tab === 'resources' && <Resources dep={d} />}
      {tab === 'runs'      && <Runs runs={runs} />}
      {tab === 'workflow'  && (
        <WorkflowTab
          deployment={d}
          definition={workflowDef}
          loading={workflowLoading}
        />
      )}
    </div>
  );
}

function Breadcrumb() {
  return (
    <div style={{ fontSize: 12, color: T.text3, marginBottom: 4 }}>
      <Link to="/cloud-operations/deployments" style={{ color: T.text2 }}>Deployments</Link>
      <span style={{ margin: '0 6px' }}>/</span>
    </div>
  );
}

function Header({ dep, target, onBack }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 20 }}>
      <div style={{ flex: 1 }}>
        <h2 style={{ margin: 0, fontSize: 22, color: T.ink, fontWeight: 700 }}>{dep.name}</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <StatusPill status={dep.status} />
          {target && <ModePill mode={target.mode} />}
          {target && <code style={{ fontSize: 12, color: T.text2 }}>{target.primary_region}</code>}
          {target?.paired_region && <code style={{ fontSize: 12, color: T.text2 }}>⇄ {target.paired_region}</code>}
          <code style={{ fontSize: 12, color: T.text2 }}>{dep.platform_version || '—'}</code>
          <code style={{ fontSize: 12, color: T.text2 }}>helm rev {dep.helm_release_revision ?? '—'}</code>
        </div>
      </div>
      <button onClick={onBack} style={btn}>← Back to list</button>
    </div>
  );
}

function WorkflowTab({ deployment, definition, loading }) {
  if (!deployment?.workflow_source_id) {
    return (
      <Banner color={T.warn} bg={T.warnBg}>
        No System Deployment Workflow recorded on this deployment.
      </Banner>
    );
  }
  if (loading && !definition) {
    return <Banner color={T.info} bg={T.infoBg}>Loading workflow definition…</Banner>;
  }
  if (!definition) {
    return (
      <Banner color={T.warn} bg={T.warnBg}>
        Workflow {deployment.workflow_source_id} has no definition payload — the catalog row may be a stub.
      </Banner>
    );
  }
  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 13, color: T.text2 }}>
        Read-only view of <code>{deployment.workflow_source_id}</code>
        {deployment.workflow_source_version && (
          <span> · v{deployment.workflow_source_version}</span>
        )}.
        Click a node to inspect its inputs / connector / retry policy.
      </div>
      <WorkflowCanvasReadView definition={definition} />
    </div>
  );
}

function Tabs({ current, onChange }) {
  const tabs = [
    { id: 'overview',  label: 'Overview' },
    { id: 'resources', label: 'Resources' },
    { id: 'runs',      label: 'Workflow runs' },
    { id: 'workflow',  label: 'Workflow' },     // read-view of the DAG
  ];
  return (
    <div style={{
      display: 'flex', gap: 2, borderBottom: `1px solid ${T.stroke}`, marginBottom: 16,
    }}>
      {tabs.map((tb) => (
        <button key={tb.id} onClick={() => onChange(tb.id)} style={{
          padding: '8px 14px', fontSize: 13, fontWeight: 600,
          color: current === tb.id ? T.ink : T.text2,
          background: 'transparent', border: 'none',
          borderBottom: `2px solid ${current === tb.id ? T.action : 'transparent'}`,
          marginBottom: -1, cursor: 'pointer',
        }}>{tb.label}</button>
      ))}
    </div>
  );
}

function Overview({ dep, target, runs }) {
  const latest = runs[0];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 320px', gap: 20 }}>
      <div>
        <Card title="Health snapshot">
          {dep.status === 'pending' || dep.status === 'provisioning' ? (
            <Banner color={T.info} bg={T.infoBg}>
              Deployment is <strong>{dep.status}</strong>. Health snapshot becomes available
              once the bring-up workflow completes the smoke window (step 18 of <code>gcp.deploy.bring-up-cluster</code>).
            </Banner>
          ) : (
            <pre style={preStyle}>{JSON.stringify(dep.health_summary || {}, null, 2)}</pre>
          )}
        </Card>

        <Card title="Latest workflow run">
          {!latest && <div style={{ color: T.text3, fontStyle: 'italic' }}>No runs yet.</div>}
          {latest && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 12 }}>
              <Mini label="Workflow"     value={<code style={{ fontSize: 11 }}>{shortId(latest.workflow_id)}</code>} />
              <Mini label="Version"      value={<code style={{ fontSize: 11 }}>{latest.workflow_version}</code>} />
              <Mini label="Result"       value={<StatusPill status={latest.result} scheme="run" />} />
              <Mini label="Progress"     value={`${latest.completed_steps} / ${latest.total_steps}`} />
              <Mini label="Current step" value={latest.current_step_name || '—'} />
              <Mini label="Started"      value={fmtDate(latest.started_at)} />
            </div>
          )}
        </Card>
      </div>

      <Sidebar>
        <h4 style={{ margin: '0 0 12px', fontSize: 13, color: T.ink, fontWeight: 600 }}>Deployment metadata</h4>
        <Meta label="Deployment ID"        value={<code style={{ fontSize: 11 }}>{dep.id}</code>} />
        <Meta label="Target"               value={<code style={{ fontSize: 11 }}>{shortId(dep.target_id)}</code>} />
        <Meta label="Mode"                 value={target ? <ModePill mode={target.mode} /> : '—'} />
        <Meta label="Helm release"         value={<code style={{ fontSize: 11 }}>{dep.helm_release_name}</code>} />
        <Meta label="Helm revision"        value={dep.helm_release_revision ?? '—'} />
        <Meta label="Source workflow"      value={<code style={{ fontSize: 11 }}>{shortId(dep.workflow_source_id)}</code>} />
        <Meta label="Source version"       value={<code style={{ fontSize: 11 }}>{dep.workflow_source_version || '—'}</code>} />
        <Meta label="Created"              value={fmtDate(dep.created_at)} />
        <Meta label="Created by"           value={<code style={{ fontSize: 11 }}>{shortId(dep.created_by)}</code>} />
        <Meta label="Last audit anchor"    value={<code style={{ fontSize: 11 }}>{dep.last_audit_anchor || 'pending'}</code>} />
      </Sidebar>
    </div>
  );
}

function Resources({ dep }) {
  const handles = dep.resource_handles || {};
  const entries = Object.entries(handles);
  return (
    <Card title="Cloud resource handles">
      {entries.length === 0 ? (
        <div style={{ color: T.text3, fontStyle: 'italic' }}>
          Resource handles populate as the bring-up workflow creates them.
          Each step of <code>gcp.deploy.bring-up-cluster</code> writes back to this map
          (gke_cluster, sql_app, sql_temporal, artifact_registry, dns_zone, managed_cert, …).
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: T.bgTint }}><Th>Kind</Th><Th>Resource handle</Th></tr></thead>
          <tbody>
            {entries.map(([k, v]) => (
              <tr key={k} style={{ borderTop: `1px solid ${T.stroke2}` }}>
                <Td><code style={{ fontSize: 12 }}>{k}</code></Td>
                <Td><code style={{ fontSize: 12 }}>{typeof v === 'string' ? v : JSON.stringify(v)}</code></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

function Runs({ runs }) {
  return (
    <Card title={`Workflow runs (${runs.length})`}>
      {runs.length === 0
        ? <div style={{ color: T.text3, fontStyle: 'italic' }}>No runs recorded.</div>
        : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: T.bgTint }}>
                <Th>Run</Th><Th>Workflow</Th><Th>Started</Th>
                <Th>Progress</Th><Th>Current step</Th><Th>Result</Th><Th>Audit</Th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id} style={{ borderTop: `1px solid ${T.stroke2}` }}>
                  <Td><code style={{ fontSize: 11 }}>{shortId(r.id)}</code></Td>
                  <Td><code style={{ fontSize: 11 }}>{shortId(r.workflow_id)}</code><div style={{ color: T.text3, fontSize: 11 }}>v{r.workflow_version}</div></Td>
                  <Td>{fmtDate(r.started_at)}</Td>
                  <Td>{r.completed_steps} / {r.total_steps}</Td>
                  <Td>{r.current_step_name || '—'}</Td>
                  <Td><StatusPill status={r.result} scheme="run" /></Td>
                  <Td><code style={{ fontSize: 11 }}>{r.audit_anchor || '—'}</code></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      }
    </Card>
  );
}

// ─── primitives ──────────────────────────────────────────────────────────────
function Card({ title, children }) {
  return (
    <div style={{
      background: T.bgCard, border: `1px solid ${T.stroke}`, borderRadius: 6,
      marginBottom: 16,
    }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${T.stroke2}` }}>
        <h3 style={{ margin: 0, fontSize: 14, color: T.ink, fontWeight: 600 }}>{title}</h3>
      </div>
      <div style={{ padding: 18 }}>{children}</div>
    </div>
  );
}
function Sidebar({ children }) {
  return (
    <div style={{
      background: T.bgTint, border: `1px solid ${T.stroke}`, borderRadius: 6,
      padding: 18, fontSize: 13, alignSelf: 'start',
    }}>{children}</div>
  );
}
function Meta({ label, value }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', color: T.text3, letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
      <div style={{ marginTop: 2, color: T.text }}>{value}</div>
    </div>
  );
}
function Mini({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: T.text3, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 600 }}>{label}</div>
      <div style={{ marginTop: 2 }}>{value}</div>
    </div>
  );
}

const preStyle = {
  fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11,
  background: T.bgTint, border: `1px solid ${T.stroke}`, borderRadius: 4,
  padding: '8px 12px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
  maxHeight: 280, overflow: 'auto',
};
